import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

export type SurveySignupStepIndex = 1 | 2 | 3

/** Figma 136:4391 — outer ring */
const ACTIVE_RING_PX = 51
/** Figma 136:4392 — inner fill */
const ACTIVE_INNER_PX = 42.62
/** Figma 136:4398 — inactive disc */
const INACTIVE_PX = 50
const SLOT_PX = ACTIVE_RING_PX

const ACTIVE_AMBER = '#F59E0B'
const LABEL_ORANGE = '#FE8120'
const CONNECTOR_ORANGE = '#FF9933'
const CONNECTOR_GRAY = '#D0D0D0'
/** Opaque mix of Figma rgba(208,208,208,0.5) on white — line must not show through. */
const INACTIVE_FILL = '#E8E8E8'
const INACTIVE_NUMBER = '#7A797E'
const INACTIVE_LABEL = '#5A5656'

/** Figma 136:4386 — compact 3-step track width */
const COMPACT_TRACK_PX = 424
const COMPACT_STEP_INDEXES: SurveySignupStepIndex[] = [1, 2, 3]

const STEPS: { index: SurveySignupStepIndex; label: string }[] = [
  { index: 1, label: 'Verify Mobile Number' },
  { index: 2, label: 'Create Your Account' },
  { index: 3, label: 'Redeem Our Reward' },
]

function stepLabel(index: SurveySignupStepIndex, compactLabels: boolean): string {
  if (!compactLabels) {
    return STEPS[index - 1].label
  }
  if (index === 1) return 'Mobile Verification'
  if (index === 2) return 'Create Account'
  return 'Redeem Reward'
}

/** Orange on 1→2 always; 2→3 only after account is the current/completed step. */
function connectorIsOrange(fromStep: SurveySignupStepIndex, activeStep: SurveySignupStepIndex): boolean {
  return fromStep === 1 || fromStep < activeStep
}

function dashedConnectorFill(orange: boolean) {
  const color = orange ? CONNECTOR_ORANGE : CONNECTOR_GRAY
  return `repeating-linear-gradient(90deg, ${color} 0 2px, transparent 2px 4px)`
}

function gridConnectorSx(orange: boolean) {
  return {
    position: 'absolute',
    top: SLOT_PX / 2 - 1,
    left: `calc(50% + ${SLOT_PX / 2}px)`,
    width: `calc(100% - ${SLOT_PX}px)`,
    height: '2px',
    backgroundImage: dashedConnectorFill(orange),
  } as const
}

const numberSx = {
  fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
  fontWeight: 700,
  fontSize: 20,
  letterSpacing: '0.1262px',
  lineHeight: 1,
} as const

const compactLabelSx = {
  fontFamily: 'Roboto, Inter, sans-serif',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '18px',
  letterSpacing: '0.28px',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',
  overflow: 'visible',
  mt: '11px',
  mb: 0,
  width: '100%',
} as const

function compactLabelAlign(index: SurveySignupStepIndex): 'left' | 'center' | 'right' {
  if (index === 1) return 'left'
  if (index === 3) return 'right'
  return 'center'
}

interface SurveySignupStepperProps {
  readonly activeStep: SurveySignupStepIndex
  readonly compactLabels?: boolean
}

function StepCircle({
  index,
  reached,
  isCurrent,
}: Readonly<{ index: SurveySignupStepIndex; reached: boolean; isCurrent: boolean }>) {
  return (
    <Box
      aria-current={isCurrent ? 'step' : undefined}
      data-testid={`survey-signup-step-${index}`}
      data-reached={reached ? 'true' : 'false'}
      sx={{
        width: SLOT_PX,
        height: SLOT_PX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 2,
        bgcolor: '#FFFFFF',
        borderRadius: '50%',
      }}
    >
      {reached ? (
        <Box
          sx={{
            width: ACTIVE_RING_PX,
            height: ACTIVE_RING_PX,
            borderRadius: '50%',
            border: `2px solid ${ACTIVE_AMBER}`,
            bgcolor: '#FFFFFF',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: ACTIVE_INNER_PX,
              height: ACTIVE_INNER_PX,
              borderRadius: '50%',
              bgcolor: ACTIVE_AMBER,
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...numberSx,
            }}
          >
            {index}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: INACTIVE_PX,
            height: INACTIVE_PX,
            borderRadius: '50%',
            bgcolor: INACTIVE_FILL,
            backgroundColor: INACTIVE_FILL,
            color: INACTIVE_NUMBER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...numberSx,
          }}
        >
          {index}
        </Box>
      )}
    </Box>
  )
}

function CompactSurveySignupStepper({ activeStep }: Readonly<{ activeStep: SurveySignupStepIndex }>) {
  return (
    <Box
      data-testid="survey-signup-stepper"
      data-active-step={activeStep}
      data-compact="true"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: COMPACT_TRACK_PX,
        mx: 'auto',
        flexShrink: 0,
        overflow: 'visible',
      }}
    >
      <Box
        aria-hidden
        data-testid="survey-signup-connector-1"
        data-tone={connectorIsOrange(1, activeStep) ? 'orange' : 'gray'}
        sx={{
          position: 'absolute',
          top: SLOT_PX / 2 - 1,
          left: `calc(16.666% + ${SLOT_PX / 2}px)`,
          width: `calc(33.333% - ${SLOT_PX}px)`,
          height: '2px',
          zIndex: 0,
          backgroundImage: dashedConnectorFill(connectorIsOrange(1, activeStep)),
        }}
      />
      <Box
        aria-hidden
        data-testid="survey-signup-connector-2"
        data-tone={connectorIsOrange(2, activeStep) ? 'orange' : 'gray'}
        sx={{
          position: 'absolute',
          top: SLOT_PX / 2 - 1,
          left: `calc(50% + ${SLOT_PX / 2}px)`,
          width: `calc(33.333% - ${SLOT_PX}px)`,
          height: '2px',
          zIndex: 0,
          backgroundImage: dashedConnectorFill(connectorIsOrange(2, activeStep)),
        }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {COMPACT_STEP_INDEXES.map((index) => {
          const reached = index <= activeStep
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <StepCircle
                index={index}
                reached={reached}
                isCurrent={index === activeStep}
              />
              <Typography
                component="p"
                sx={{
                  ...compactLabelSx,
                  fontSize: { xs: 11, sm: 14 },
                  textAlign: compactLabelAlign(index),
                  color: reached ? LABEL_ORANGE : INACTIVE_LABEL,
                }}
              >
                {stepLabel(index, true)}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export function SurveySignupStepper({
  activeStep,
  compactLabels = false,
}: SurveySignupStepperProps) {
  if (compactLabels) {
    return <CompactSurveySignupStepper activeStep={activeStep} />
  }

  return (
    <Box
      data-testid="survey-signup-stepper"
      data-active-step={activeStep}
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        width: '100%',
        mb: 3,
      }}
    >
      {STEPS.map((step, i) => {
        const reached = step.index <= activeStep
        const isLast = i === STEPS.length - 1
        const connectorOrange = connectorIsOrange(step.index, activeStep)
        return (
          <Box
            key={step.index}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '17px',
            }}
          >
            <StepCircle
              index={step.index}
              reached={reached}
              isCurrent={step.index === activeStep}
            />
            {!isLast ? (
              <Box
                aria-hidden
                data-testid={`survey-signup-connector-${step.index}`}
                data-tone={connectorOrange ? 'orange' : 'gray'}
                sx={gridConnectorSx(connectorOrange)}
              />
            ) : null}
            <Typography
              component="p"
              sx={{
                fontFamily: 'Roboto, Inter, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            lineHeight: { xs: '16px', md: '10px' },
            letterSpacing: '0.28px',
            textAlign: 'center',
            color: reached ? LABEL_ORANGE : INACTIVE_LABEL,
            textTransform: 'capitalize',
            maxWidth: { xs: '100%', md: 123 },
            px: { xs: 0.5, md: 0 },
            whiteSpace: { xs: 'normal', md: 'nowrap' },
              }}
            >
              {stepLabel(step.index, false)}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
